// Sesión de empleado (PIN) tras recargar el TPV compartido.
//
// Regla del producto (2026-10-02): al recargar se mantiene SIEMPRE, en
// silencio, el empleado que estaba identificado (permisos, auditoría,
// presencia). Solo se pide el PIN si el empleado se ha quedado sin
// identificación válida (sesión caducada o revocada en el servidor).
//
// Antes, `getInitialize` borraba el token firmado en las sesiones del
// propietario para limpiar "restos" de un empleado anterior: la UI seguía
// mostrando al empleado (currentActiveEmployee persistido) pero las
// peticiones salían como el dueño. Ahora se distingue un resto (el dueño
// opera, o el token es de otra sesión web) de una sesión viva.

import axios from "src/utils/axios";
import {
  clearSession,
  issueEmployeeSessionWithoutPin,
  openPinDialog,
  postSessionRefresh,
} from "src/redux/slices/employeeSession";
import { getDeviceId } from "src/utils/deviceId";
import { openKiosk, setKioskNotice } from "src/redux/slices/employee";
import { isQuietEnd } from "src/utils/employeeSessionEvents";

export const OWNER_MARKER_KEY = "EmployeeSessionOwner";

// Sesión web a la que pertenece la sesión del empleado: usuario + id de
// sesión del navegador. Un token de otro login es un resto, no una sesión.
export function currentOwnerMarker(storage = window.localStorage) {
  try {
    const userId = storage.getItem("userId");
    const sessionIdentifier = storage.getItem("sessionIdentifier");
    if (!userId || !sessionIdentifier) return null;
    return `${userId}|${sessionIdentifier}`;
  } catch {
    return null;
  }
}

/**
 * Decide qué hacer con la sesión del empleado al arrancar (función pura).
 * Devuelve:
 *   { action: "none" }               sesión web de empleado o nada que hacer
 *   { action: "clear" }              resto de otra sesión / el dueño opera
 *   { action: "keep", employeeId }   token válido del empleado activo → revalidar
 *   { action: "reissue", employeeId } había empleado activo sin token válido
 */
export function decideEmployeeSessionOnBoot({
  sessionKind,
  token,
  expiresAt,
  info,
  ownerMarker,
  currentMarker,
  currentActiveEmployee,
  now = new Date(),
}) {
  if (sessionKind === "employee-web") return { action: "none" };

  const tokenValid = !!token && (!expiresAt || new Date(expiresAt) > now);
  const tokenEmployee = Number(info?.employeeId) || 0;
  const markerMismatch = !!ownerMarker && !!currentMarker && ownerMarker !== currentMarker;
  // null/undefined = nunca se eligió; 0 = el dueño opera explícitamente.
  const hasActive = currentActiveEmployee !== null && currentActiveEmployee !== undefined;
  const active = Number(currentActiveEmployee) || 0;

  if (markerMismatch) {
    return active > 0 ? { action: "reissue", employeeId: active } : { action: "clear" };
  }
  if (hasActive && active === 0) return { action: "clear" };

  if (tokenValid && tokenEmployee > 0) {
    if (!hasActive || active === tokenEmployee) return { action: "keep", employeeId: tokenEmployee };
    // El token es de otro empleado: no sirve para el que está en pantalla.
    return { action: "reissue", employeeId: active };
  }
  if (active > 0) return { action: "reissue", employeeId: active };
  return token ? { action: "clear" } : { action: "none" };
}

// Código de error que confirma que la sesión ya no es válida en el
// servidor (frente a un fallo de red, en el que se conserva).
const DEAD_SESSION_CODES = ["NO_ACTIVE_SESSION", "EMPLOYEE_INACTIVE", "EMPLOYEE_SESSION_EXPIRED"];
export function isDeadSessionError(err) {
  const code = err?.code || err?.response?.data?.code;
  return DEAD_SESSION_CODES.includes(code);
}

function applyEmployeeIdentity(dispatch, employeeId) {
  try { window.localStorage.setItem("UserEmployeeId", employeeId); } catch { /* ignore */ }
  axios.defaults.headers.common.UserEmployeeId = employeeId;
  dispatch({ type: "employees/changeEmployee", payload: employeeId });
}

export function clearEmployeeIdentity(dispatch) {
  try { window.localStorage.setItem("UserEmployeeId", 0); } catch { /* ignore */ }
  axios.defaults.headers.common.UserEmployeeId = 0;
  dispatch(clearSession());
}

// Sin sesión válida para el empleado en pantalla: si no tiene PIN se
// re-emite en silencio; si lo tiene, se le pide que se identifique.
export function reidentify(employeeId) {
  return async (dispatch, getState) => {
    // El diálogo de PIN ya está pidiendo identificarse: no se re-emite ni se
    // reabre en cada comprobación (focus, visibilidad, cada 5 min…).
    if (getState?.()?.employeeSession?.pinDialog?.open) return "pin";
    clearEmployeeIdentity(dispatch);
    let issued = null;
    try {
      issued = await dispatch(issueEmployeeSessionWithoutPin(employeeId, getDeviceId()));
    } catch { /* cae al PIN */ }
    if (issued?.token) {
      applyEmployeeIdentity(dispatch, employeeId);
      return "reissued";
    }
    dispatch(
      openPinDialog({
        reason: "Tu sesión ha caducado. Vuelve a identificarte con tu PIN.",
        onSuccess: (res) => {
          const id = Number(res?.session?.employeeId) || 0;
          if (id > 0) applyEmployeeIdentity(dispatch, id);
        },
      }),
    );
    return "pin";
  };
}

// Revalida en silencio contra el servidor (renueva el token). Solo se
// descarta la sesión si el servidor la rechaza; un fallo de red la conserva.
/**
 * 2026-10-06 · La sesión no murió sola: el empleado entró en otro dispositivo
 * (con «un empleado en un dispositivo»), un responsable la cerró o el empleado
 * fue dado de baja. Entonces NO se re-identifica (con un empleado sin PIN eso
 * re-emitiría la sesión aquí y expulsaría al otro dispositivo: ping-pong);
 * se vuelve al kiosko con el aviso. Null si no aplica.
 */
export function quietEndNotice(err) {
  const body = err?.response?.data || err || {};
  if (!isQuietEnd(body.reason)) return null;
  return { reason: body.reason, employeeName: body.employeeName || null, deviceName: body.deviceName || null };
}

export function revalidateEmployeeSession(employeeId) {
  return async (dispatch, getState) => {
    try {
      await postSessionRefresh(dispatch);
      return "valid";
    } catch (err) {
      if (!isDeadSessionError(err)) return "unknown";
      const notice = quietEndNotice(err);
      if (notice) {
        clearEmployeeIdentity(dispatch);
        if (!getState?.()?.employees?.kioskOpen) dispatch(openKiosk({ manual: true, notice }));
        else dispatch(setKioskNotice(notice));
        return "kiosk";
      }
      return dispatch(reidentify(employeeId));
    }
  };
}

/**
 * Arranque: mantiene la sesión del empleado que estaba identificado.
 * `getState().employees.currentActiveEmployee` ya está rehidratado
 * (PersistGate envuelve a JwtProvider).
 */
export function restoreEmployeeSessionOnBoot({ clearResidue }) {
  return async (dispatch, getState) => {
    let decision;
    try {
      const ls = window.localStorage;
      decision = decideEmployeeSessionOnBoot({
        sessionKind: ls.getItem("SessionKind"),
        token: ls.getItem("EmployeeSessionToken"),
        expiresAt: ls.getItem("EmployeeSessionExpiresAt"),
        info: JSON.parse(ls.getItem("EmployeeSessionInfo") || "null"),
        ownerMarker: ls.getItem(OWNER_MARKER_KEY),
        currentMarker: currentOwnerMarker(ls),
        currentActiveEmployee: getState()?.employees?.currentActiveEmployee,
      });
    } catch {
      decision = { action: "clear" };
    }

    switch (decision.action) {
      case "clear":
        clearResidue(dispatch);
        return decision;
      case "keep":
        applyEmployeeIdentity(dispatch, decision.employeeId);
        await dispatch(revalidateEmployeeSession(decision.employeeId));
        return decision;
      case "reissue":
        await dispatch(reidentify(decision.employeeId));
        return decision;
      default:
        return decision;
    }
  };
}
